import { createContext, useState, useEffect } from "react";
import axios from "axios";

export const AuthContext = createContext();

const BASE_URL = process.env.REACT_APP_API_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set axios default headers
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["x-auth-token"] = token;
    } else {
      delete axios.defaults.headers.common["x-auth-token"];
    }
  }, [token]);

  useEffect(() => {
    const loadUser = async () => {
      if (!token || document.visibilityState !== "visible") {
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(`${BASE_URL}/api/auth/user`);
        setUser(res.data);
        setIsAuthenticated(true);
        setError(null);
      } catch (err) {
        localStorage.removeItem("token");
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
        setError(err.response?.data?.message || "Authentication error");
      } finally {
        setLoading(false);
      }
    };

    // Load on visibility change or mount
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        loadUser();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Initial load
    loadUser();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [token]);

  // Register user
  const register = async (formData) => {
    try {
      const res = await axios.post(`${BASE_URL}/api/auth/register`, formData);
      localStorage.setItem("token", res.data.token);
      setToken(res.data.token);
      setUser(res.data.user);
      setIsAuthenticated(true);
      setError(null);
      return true;
    } catch (err) {
      setError(err.response?.data?.message || "Registration failed");
      return false;
    }
  };

  // Login user
  const login = async (formData) => {
    try {
      const res = await axios.post(`${BASE_URL}/api/auth/login`, formData);
      localStorage.setItem("token", res.data.token);
      setToken(res.data.token);
      setUser(res.data.user);
      setIsAuthenticated(true);
      setError(null);
      return true;
    } catch (err) {
      setError(err.response?.data?.message || "Login failed");
      return false;
    }
  };

  // Logout user
  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        loading,
        error,
        register,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
